import { ApiError } from './apiError';
import type { AccessTokenProvider } from './authTokenProvider';
import type { ApiRequestInit, FetchImplementation } from './types';

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly tokenProvider: AccessTokenProvider,
    private readonly fetchImplementation: FetchImplementation = fetch,
  ) {}

  async request(path: string, options: ApiRequestInit = {}) {
    const { auth = 'public', ...requestInit } = options;
    const url = new URL(path, this.baseUrl);
    if (auth === 'public') return this.fetchImplementation(url, requestInit);
    if (url.origin !== new URL(this.baseUrl).origin) {
      throw new ApiError(
        'Protected requests must target the configured API origin.',
        'request-failed',
      );
    }

    const token = await this.tokenProvider.getAccessToken();
    if (!token)
      throw new ApiError(
        'Authentication is required before this request can be sent.',
        'authentication-required',
        401,
      );
    const response = await this.sendWithToken(url, requestInit, token);
    if (response.status !== 401) return response;

    const refreshedToken = await this.tokenProvider.getAccessToken({ forceRefresh: true });
    if (!refreshedToken) return response;
    return this.sendWithToken(url, requestInit, refreshedToken);
  }

  private sendWithToken(url: URL, requestInit: RequestInit, token: string) {
    const headers = new Headers(requestInit.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return this.fetchImplementation(url, { ...requestInit, headers });
  }
}
